import { array, boolean, discriminatedUnion, email, enum as enumFactory, iso, literal, number, strictObject, string } from "../../lazy-zod.js";
import { MESSAGE_CHANNEL_PROVIDERS, messageChannelIngressEnvelopeSchema, messageChannelProviderSchema, messageChannelTransportSchema } from "../runtime/message-channel.js";
import lark_platform_registration_domains_default from "./lark-platform-registration-domains.js";
//#region src/schemas/http/message-channels.ts
const LARK_PLATFORM_REGISTRATION_DOMAINS = lark_platform_registration_domains_default;
const MESSAGE_CHANNEL_CONNECTION_STATUSES = [
	"pending_registration",
	"credentials_received",
	"configuring",
	"connected",
	"repair_required",
	"revoked",
	"disconnected"
];
const MESSAGE_CHANNEL_ERROR_CODES = [
	"message_channel.actor_required",
	"message_channel.already_connected",
	"message_channel.connection_changed",
	"message_channel.identity_already_linked",
	"message_channel.identity_link_account_required",
	"message_channel.identity_link_membership_required",
	"message_channel.identity_link_not_found",
	"message_channel.identity_link_token_invalid",
	"message_channel.organization_required",
	"message_channel.provider_disabled",
	"message_channel.provider_unavailable",
	"message_channel.repair_unavailable",
	"message_channel.registration_not_found",
	"message_channel.store_unavailable",
	"message_channel.setup_request_already_connected",
	"message_channel.setup_request_forbidden",
	"message_channel.setup_request_recipient_unavailable"
];
const LARK_PLATFORM_WEBHOOK_RAW_BODY_MAX_BYTES = 262144;
const LARK_PLATFORM_WEBHOOK_RAW_BODY_MAX_CHARS = LARK_PLATFORM_WEBHOOK_RAW_BODY_MAX_BYTES;
const nonEmptyStringSchema = string().trim().min(1);
const capabilityGroupSchema = nonEmptyStringSchema.max(64).regex(/^[a-z0-9][a-z0-9._-]*$/u);
const reasonCodeSchema = nonEmptyStringSchema.max(128).regex(/^[a-z0-9][a-z0-9._-]*$/u);
const messageChannelConnectionStatusSchema = enumFactory(MESSAGE_CHANNEL_CONNECTION_STATUSES);
const messageChannelSetupRequestBodySchema = strictObject({ provider: messageChannelProviderSchema });
const messageChannelSetupRequestResponseSchema = strictObject({
	organizationId: nonEmptyStringSchema.max(256),
	provider: messageChannelProviderSchema,
	recipientCount: number().int().positive(),
	requestId: nonEmptyStringSchema.max(256),
	status: enumFactory(["created", "already_requested"])
});
const messageChannelIdentityLinkIdSchema = nonEmptyStringSchema.max(82).regex(/^channel_identity_[0-9a-f]{64}$/u);
const larkPlatformProviderSchema = enumFactory(["lark", "feishu"]);
const larkPlatformRegistrationBeginModeSchema = enumFactory([
	"resume",
	"restart",
	"repair",
	"reauthorize"
]);
const messageChannelConnectionProjectionSchema = strictObject({
	id: nonEmptyStringSchema.max(256),
	provider: messageChannelProviderSchema,
	transport: messageChannelTransportSchema,
	status: messageChannelConnectionStatusSchema,
	externalAccountId: nonEmptyStringSchema.max(512).nullable(),
	externalAccountName: nonEmptyStringSchema.max(256).nullable(),
	permissionBundleVersion: nonEmptyStringSchema.max(128).nullable(),
	grantedCapabilityGroups: array(capabilityGroupSchema).max(32),
	lastVerifiedAt: iso.datetime({ offset: true }).nullable(),
	reasonCode: reasonCodeSchema.nullable()
}).superRefine((connection, context) => {
	if (new Set(connection.grantedCapabilityGroups).size !== connection.grantedCapabilityGroups.length) context.addIssue({
		code: "custom",
		message: "Granted capability groups must be unique.",
		path: ["grantedCapabilityGroups"]
	});
});
const messageChannelIdentityLinkProjectionSchema = strictObject({
	connectedAt: iso.datetime({ offset: true }),
	id: messageChannelIdentityLinkIdSchema,
	openMeldUserId: nonEmptyStringSchema.max(256),
	provider: messageChannelProviderSchema,
	providerAccountEmail: email().max(320).nullable().default(null),
	providerAccountName: nonEmptyStringSchema.max(256).nullable()
});
const messageChannelConnectionListResponseSchema = strictObject({
	organizationId: nonEmptyStringSchema.max(256),
	availableLarkPlatformProviders: array(larkPlatformProviderSchema).max(2),
	connections: array(messageChannelConnectionProjectionSchema).max(MESSAGE_CHANNEL_PROVIDERS.length),
	identityLinks: array(messageChannelIdentityLinkProjectionSchema).max(1e4).default([])
}).superRefine((response, context) => {
	if (new Set(response.availableLarkPlatformProviders).size !== response.availableLarkPlatformProviders.length) context.addIssue({
		code: "custom",
		message: "Available Message Channel providers must be unique.",
		path: ["availableLarkPlatformProviders"]
	});
	const providers = /* @__PURE__ */ new Set();
	for (const [index, connection] of response.connections.entries()) {
		if (providers.has(connection.provider)) context.addIssue({
			code: "custom",
			message: "An Organization can expose at most one connection per Message Channel provider.",
			path: [
				"connections",
				index,
				"provider"
			]
		});
		providers.add(connection.provider);
	}
});
const larkPlatformRegistrationAttemptIdSchema = nonEmptyStringSchema.max(256);
const larkPlatformActiveRegistrationProjectionSchema = strictObject({
	canResume: boolean(),
	expiresAt: iso.datetime({ offset: true }),
	id: larkPlatformRegistrationAttemptIdSchema,
	nextPollAt: iso.datetime({ offset: true }),
	provider: larkPlatformProviderSchema,
	reasonCode: reasonCodeSchema.nullable(),
	status: enumFactory([
		"pending_registration",
		"credentials_received",
		"configuring"
	])
});
const larkPlatformActiveRegistrationListResponseSchema = strictObject({
	organizationId: nonEmptyStringSchema.max(256),
	registrations: array(larkPlatformActiveRegistrationProjectionSchema).max(2)
}).superRefine((response, context) => {
	const providers = /* @__PURE__ */ new Set();
	for (const [index, registration] of response.registrations.entries()) {
		if (providers.has(registration.provider)) context.addIssue({
			code: "custom",
			message: "An Organization can expose at most one active registration per Lark-platform provider.",
			path: [
				"registrations",
				index,
				"provider"
			]
		});
		providers.add(registration.provider);
	}
});
const larkPlatformRegistrationAttemptProjectionSchema = strictObject({
	id: larkPlatformRegistrationAttemptIdSchema,
	provider: larkPlatformProviderSchema,
	status: messageChannelConnectionStatusSchema,
	expiresAt: iso.datetime({ offset: true }),
	nextPollAt: iso.datetime({ offset: true }),
	registrationUrl: string().trim().max(16384).url().nullable(),
	reasonCode: reasonCodeSchema.nullable()
}).superRefine((attempt, context) => {
	if (attempt.status === "pending_registration" && attempt.registrationUrl === null || attempt.registrationUrl !== null && attempt.status !== "pending_registration" && !(attempt.status === "configuring" && (attempt.reasonCode === `${attempt.provider}.application_config_permission_required` || attempt.reasonCode === `${attempt.provider}.authorization_update_required` || attempt.reasonCode === `${attempt.provider}.permission_bundle_mismatch`))) context.addIssue({
		code: "custom",
		message: "A registration URL is required for registration and may also be returned for explicit reauthorization.",
		path: ["registrationUrl"]
	});
	if (attempt.registrationUrl) {
		const url = new URL(attempt.registrationUrl);
		if (url.protocol !== "https:" || url.host !== lark_platform_registration_domains_default.feishu.launcherHost && url.host !== lark_platform_registration_domains_default[attempt.provider].launcherHost || url.pathname !== "/page/launcher" || url.username || url.password || url.hash || url.searchParams.getAll("user_code").length !== 1 || !url.searchParams.get("user_code")?.trim()) context.addIssue({
			code: "custom",
			message: "Registration URL must use the shared issuer or selected provider launcher.",
			path: ["registrationUrl"]
		});
	}
});
const larkPlatformRegistrationResponseSchema = strictObject({
	organizationId: nonEmptyStringSchema.max(256),
	attempt: larkPlatformRegistrationAttemptProjectionSchema,
	connection: messageChannelConnectionProjectionSchema.nullable()
}).superRefine((response, context) => {
	if (response.connection && response.connection.provider !== response.attempt.provider) context.addIssue({
		code: "custom",
		message: "Registration attempt and connection providers must match.",
		path: ["connection", "provider"]
	});
});
const larkPlatformWebhookVerificationInputSchema = strictObject({
	provider: larkPlatformProviderSchema,
	connectionId: nonEmptyStringSchema.max(256),
	surface: enumFactory(["events", "callbacks"]),
	rawBody: string().min(2).max(LARK_PLATFORM_WEBHOOK_RAW_BODY_MAX_CHARS),
	headers: strictObject({
		timestamp: nonEmptyStringSchema.max(64).nullable(),
		nonce: nonEmptyStringSchema.max(256).nullable(),
		signature: nonEmptyStringSchema.max(256).nullable()
	}),
	receivedAtMs: number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
	gatewayRequestId: nonEmptyStringSchema.max(256)
});
const larkPlatformWebhookVerificationResponseSchema = discriminatedUnion("status", [
	strictObject({
		status: literal("challenge"),
		challenge: nonEmptyStringSchema.max(2048)
	}),
	strictObject({
		status: literal("enqueue"),
		envelope: messageChannelIngressEnvelopeSchema
	}),
	strictObject({
		status: literal("ignored"),
		reasonCode: reasonCodeSchema
	}),
	strictObject({
		status: literal("rejected"),
		reasonCode: reasonCodeSchema
	})
]);
const messageChannelIdentityLinkTokenSchema = string().trim().regex(/^messagechannellink_[0-9a-f]{64}$/);
const messageChannelIdentityLinkPreviewBodySchema = strictObject({ token: messageChannelIdentityLinkTokenSchema });
const messageChannelIdentityLinkPreviewResponseSchema = strictObject({
	alreadyMember: boolean().default(true),
	continuesRequest: boolean().default(false),
	expiresAt: iso.datetime({ offset: true }),
	organizationId: nonEmptyStringSchema.max(256),
	organizationName: nonEmptyStringSchema.max(256),
	provider: larkPlatformProviderSchema,
	providerUserDisplayName: nonEmptyStringSchema.max(256),
	providerUserId: nonEmptyStringSchema.max(512),
	openMeldAccountEmail: nonEmptyStringSchema.max(320),
	replacesExistingLink: boolean().default(false)
});
const messageChannelIdentityLinkConsumeBodySchema = strictObject({ token: messageChannelIdentityLinkTokenSchema });
const messageChannelIdentityLinkConsumeResponseSchema = strictObject({
	continuationRetryable: boolean().default(false),
	continued: boolean().default(false),
	continuesRequest: boolean().default(false),
	joinedOrganization: boolean().default(false),
	linked: literal(true),
	organizationId: nonEmptyStringSchema.max(256),
	organizationName: nonEmptyStringSchema.max(256),
	provider: larkPlatformProviderSchema,
	providerUserDisplayName: nonEmptyStringSchema.max(256),
	providerUserId: nonEmptyStringSchema.max(512),
	replacedExistingLink: boolean().default(false),
	openMeldUserId: nonEmptyStringSchema.max(256)
});
//#endregion
export { LARK_PLATFORM_REGISTRATION_DOMAINS, LARK_PLATFORM_WEBHOOK_RAW_BODY_MAX_BYTES, LARK_PLATFORM_WEBHOOK_RAW_BODY_MAX_CHARS, MESSAGE_CHANNEL_CONNECTION_STATUSES, MESSAGE_CHANNEL_ERROR_CODES, larkPlatformActiveRegistrationListResponseSchema, larkPlatformActiveRegistrationProjectionSchema, larkPlatformProviderSchema, larkPlatformRegistrationAttemptIdSchema, larkPlatformRegistrationAttemptProjectionSchema, larkPlatformRegistrationBeginModeSchema, larkPlatformRegistrationResponseSchema, larkPlatformWebhookVerificationInputSchema, larkPlatformWebhookVerificationResponseSchema, messageChannelConnectionListResponseSchema, messageChannelConnectionProjectionSchema, messageChannelConnectionStatusSchema, messageChannelIdentityLinkConsumeBodySchema, messageChannelIdentityLinkConsumeResponseSchema, messageChannelIdentityLinkIdSchema, messageChannelIdentityLinkPreviewBodySchema, messageChannelIdentityLinkPreviewResponseSchema, messageChannelIdentityLinkTokenSchema, messageChannelSetupRequestBodySchema, messageChannelSetupRequestResponseSchema };

//# sourceMappingURL=message-channels.js.map